import type { QrPosterTemplate } from './qr-poster-template.model';

export interface QrPosterIconDefinition {
  key: string;
  nzType: string;
  labelKey: string;
}

export const QR_POSTER_ICON_DEFINITIONS = [
  { key: 'environment', nzType: 'environment', labelKey: 'QR_POSTER_TEMPLATE.BUILDER.ICONS.ENVIRONMENT' },
  { key: 'home', nzType: 'home', labelKey: 'QR_POSTER_TEMPLATE.BUILDER.ICONS.HOME' },
  { key: 'phone', nzType: 'phone', labelKey: 'QR_POSTER_TEMPLATE.BUILDER.ICONS.PHONE' },
  { key: 'global', nzType: 'global', labelKey: 'QR_POSTER_TEMPLATE.BUILDER.ICONS.GLOBAL' },
  { key: 'warning', nzType: 'warning', labelKey: 'QR_POSTER_TEMPLATE.BUILDER.ICONS.WARNING' },
  { key: 'fire', nzType: 'fire', labelKey: 'QR_POSTER_TEMPLATE.BUILDER.ICONS.FIRE' },
  { key: 'security', nzType: 'security-scan', labelKey: 'QR_POSTER_TEMPLATE.BUILDER.ICONS.SECURITY' },
  { key: 'tool', nzType: 'tool', labelKey: 'QR_POSTER_TEMPLATE.BUILDER.ICONS.SERVICE' },
  { key: 'thunderbolt', nzType: 'thunderbolt', labelKey: 'QR_POSTER_TEMPLATE.BUILDER.ICONS.GAS' },
  { key: 'customer-service', nzType: 'customer-service', labelKey: 'QR_POSTER_TEMPLATE.BUILDER.ICONS.SUPPORT' },
  { key: 'alert', nzType: 'alert', labelKey: 'QR_POSTER_TEMPLATE.BUILDER.ICONS.ALERT' },
  { key: 'info', nzType: 'info', labelKey: 'QR_POSTER_TEMPLATE.BUILDER.ICONS.INFO_GENERAL' },
  { key: 'safety', nzType: 'safety', labelKey: 'QR_POSTER_TEMPLATE.BUILDER.ICONS.SAFETY' },
  { key: 'caret-down', nzType: 'down', labelKey: 'QR_POSTER_TEMPLATE.BUILDER.ICONS.ARROW_DOWN' },
] as const satisfies readonly QrPosterIconDefinition[];

export type QrPosterIconName = typeof QR_POSTER_ICON_DEFINITIONS[number]['key'];

// The canonical renderer draws `caret-down` directly and renders `security`
// through its existing local fallback. These names therefore have no matching
// colorized asset key in the authenticated endpoint.
export const QR_POSTER_COLORIZED_ICON_ASSET_NAMES = [
  'alert',
  'customer-service',
  'environment',
  'fire',
  'global',
  'home',
  'info',
  'phone',
  'safety',
  'thunderbolt',
  'tool',
  'warning',
] as const satisfies readonly QrPosterIconName[];

export type QrPosterColorizedIconAssetName = typeof QR_POSTER_COLORIZED_ICON_ASSET_NAMES[number];

const iconMap = new Map<string, QrPosterIconDefinition>(
  QR_POSTER_ICON_DEFINITIONS.map((entry) => [entry.key, entry]),
);
const colorizedIconAssetNames = new Set<string>(QR_POSTER_COLORIZED_ICON_ASSET_NAMES);
const iconBlockTypes = new Set(['icon', 'iconText', 'iconMultilineText']);

export function getQrPosterIconDefinition(iconKey?: string | null): QrPosterIconDefinition | null {
  const normalized = (iconKey || '').trim().toLowerCase();
  return iconMap.get(normalized) || iconMap.get('environment') || null;
}

export function getQrPosterPreviewIconType(iconKey?: string | null): string {
  return getQrPosterIconDefinition(iconKey)?.nzType || 'environment';
}

export function getRequiredQrPosterColorizedIconAssetNames(
  template: Pick<QrPosterTemplate, 'blocks'>,
): QrPosterColorizedIconAssetName[] {
  const requiredNames = new Set<QrPosterColorizedIconAssetName>();

  for (const block of template.blocks || []) {
    if (block.hidden || !iconBlockTypes.has(block.type)) {
      continue;
    }

    const iconName = (block.content?.icon || '').trim().toLowerCase();
    if (!iconMap.has(iconName) || !colorizedIconAssetNames.has(iconName)) {
      continue;
    }

    requiredNames.add(iconName as QrPosterColorizedIconAssetName);
  }

  return Array.from(requiredNames).sort();
}
